import { IconInfo } from '@developerz/ui';
import { createEffect, createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { EnrollRefusal } from '../lib/enroll-refusal';
import type { SshEnrollQueued, SshEnrollRequest } from '../lib/fleet';
import { CodingModelLine } from './CodingModelLine';
import { EnrollRefusalAlert } from './EnrollRefusalAlert';
import { Field } from './Field';
import { Button, buttonClass } from './ui/Button';

// SSH-pull enrollment form (#1035) — presenter for <SshEnrollPanel/>: three
// coordinates and the box's coding model in, one request out, no fetch of its own.
//
// The model is `required` here although the wire makes it optional: the join
// refuses a fresh box naming none, and it refuses AFTER the mint, so an empty
// field would burn a seat slot for an hour on a bootstrap that cannot succeed.
// A re-join types the model it wants — the export overrides the box's own.
//
// The only validation here is the native one (`required`, the port input's
// own range, the model's charset `pattern`). `vetSshTarget` on the server is the arbiter — our cluster is the
// thing about to open a socket — and its refusal is what gets rendered, so
// nothing here duplicates that gate or pre-judges a host.
//
// A queued bootstrap REPLACES the form rather than leaving it armed: the box is
// not enrolled yet, and re-submitting the same coordinates would mint a second
// token and burn a second seat slot for an hour. The coordinates themselves
// re-arm on `open`, for the same reason and on the same schedule as the panel's
// queued/refusal state (`AddRepoSecretModal`'s convention): the dialog delays
// unmount for its exit animation, so a fast reopen would otherwise land on a
// pre-filled form one click away from that second mint.

export type SshEnrollFormProps = {
  /** The dialog's open state — an open re-arms the coordinates (see above). */
  readonly open: boolean;
  readonly busy: boolean;
  /** The server's line for a refused attempt; null while nothing is wrong. */
  readonly refusal: EnrollRefusal | null;
  /** Non-null once the bootstrap is queued — the box is NOT enrolled yet. */
  readonly queued: SshEnrollQueued | null;
  readonly onSubmit: (request: SshEnrollRequest) => void;
  readonly onClose: () => void;
};

/**
 * The join gate's model-id charset (api `BOX_MODEL_ID_PATTERN`), as an HTML
 * `pattern` — a hint only; surrounding whitespace is trimmed before the post.
 */
const MODEL_ID_PATTERN = '\\s*[A-Za-z0-9._:\\/\\-]+\\s*';

export function SshEnrollForm(props: SshEnrollFormProps): JSX.Element {
  const [host, setHost] = createSignal('');
  // 22 is the schema's own default, so an emptied field means "the usual port".
  const [port, setPort] = createSignal(22);
  const [user, setUser] = createSignal('');
  const [model, setModel] = createSignal('');

  createEffect(() => {
    if (props.open) {
      setHost('');
      setPort(22);
      setUser('');
      setModel('');
    }
  });

  const submit = (e: Event): void => {
    e.preventDefault();
    if (props.busy) return;
    props.onSubmit({
      host: host().trim(),
      port: port(),
      user: user().trim(),
      model: model().trim(),
    });
  };

  return (
    <Show
      when={props.queued}
      fallback={
        <form class="field-group" onSubmit={submit}>
          <p class="field__desc">{t('fleet.addbox.ssh_intro')}</p>
          <Field label={t('fleet.addbox.ssh_host')} id="ssh-enroll-host">
            {(a11y) => (
              <input
                {...a11y}
                class="input"
                type="text"
                value={host()}
                onInput={(e) => setHost(e.currentTarget.value)}
                placeholder={t('fleet.addbox.ssh_host_placeholder')}
                required
              />
            )}
          </Field>
          <Field label={t('fleet.addbox.ssh_port')} id="ssh-enroll-port">
            {(a11y) => (
              <input
                {...a11y}
                class="input"
                type="number"
                min="1"
                max="65535"
                value={port()}
                onInput={(e) => setPort(Number(e.currentTarget.value) || 22)}
              />
            )}
          </Field>
          <Field
            label={t('fleet.addbox.ssh_user')}
            id="ssh-enroll-user"
            desc={t('fleet.addbox.ssh_user_hint')}
          >
            {(a11y) => (
              <input
                {...a11y}
                class="input"
                type="text"
                value={user()}
                onInput={(e) => setUser(e.currentTarget.value)}
                placeholder={t('fleet.addbox.ssh_user_placeholder')}
                required
              />
            )}
          </Field>
          <Field
            label={t('fleet.addbox.ssh_model')}
            id="ssh-enroll-model"
            desc={t('fleet.addbox.ssh_model_hint')}
          >
            {(a11y) => (
              <input
                {...a11y}
                class="input"
                type="text"
                value={model()}
                onInput={(e) => setModel(e.currentTarget.value)}
                placeholder={t('fleet.addbox.ssh_model_placeholder')}
                pattern={MODEL_ID_PATTERN}
                maxLength={200}
                required
              />
            )}
          </Field>
          <Show when={props.refusal}>{(r) => <EnrollRefusalAlert refusal={r()} />}</Show>
          <div class="dialog__footer">
            <Button variant="outline" onClick={props.onClose}>
              {t('fleet.addbox.cancel')}
            </Button>
            <button type="submit" class={buttonClass()} disabled={props.busy}>
              {t(props.busy ? 'fleet.addbox.ssh_queueing' : 'fleet.addbox.ssh_submit')}
            </button>
          </div>
        </form>
      }
    >
      {(q) => (
        // `.field-group` is the panel's vertical rhythm — see <ConnectMachineBody/>:
        // the tab wrapper above stays unstyled so `hidden` still hides it.
        <div class="field-group">
          <div class="alert alert--icon" role="status">
            <IconInfo />
            <p class="alert__title">{t('fleet.addbox.ssh_queued_title')}</p>
            <p class="alert__desc">
              {t('fleet.addbox.ssh_queued_desc', { target: `${user()}@${host()}:${port()}` })}
            </p>
            <p class="alert__desc">{t('fleet.addbox.ssh_queued_runner', { id: q().runner_id })}</p>
          </div>
          <Show when={q().coding_model}>{(cm) => <CodingModelLine model={cm()} exported />}</Show>
          <div class="dialog__footer">
            <Button onClick={props.onClose}>{t('fleet.addbox.done')}</Button>
          </div>
        </div>
      )}
    </Show>
  );
}
